import { SITUACAO_PARECER_MAP } from "@/constants/SITUACAO_PARECER_MAP";
import { usePermissao } from "@/hooks/usePermissao";
import { useRelatorioAM } from "@/hooks/useRelatorioAM";
import { useQuery } from "@tanstack/react-query";
import {
  App,
  Button,
  Card,
  Col,
  Divider,
  List,
  Modal,
  Row,
  Skeleton,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { saveAs } from "file-saver";

export function ModalPlanoAcaoRelatorioAM({
  open,
  onClose,
  ramItemId,
}: {
  open: boolean;
  onClose: () => void;
  ramItemId: number;
}) {
  const { message } = App.useApp();
  const { hasEnabledPermissao } = usePermissao();
  const { getPlanoAcaoByItemIdApi, getAnexoPlanoAcaoApi } = useRelatorioAM();

  const {
    data: planosAcao,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["plano-acao-ram", open, ramItemId],
    enabled: open,
    queryFn: async () => {
      const result = await getPlanoAcaoByItemIdApi(ramItemId);
      return result;
    },
  });

  const handleOpenAnexo = async (nomeUpload?: string) => {
    if (!nomeUpload) return;

    try {
      const anexoBlob = await getAnexoPlanoAcaoApi(nomeUpload);
      saveAs(anexoBlob, nomeUpload || "anexo");
    } catch (error) {
      message.error("Não foi possível abrir o anexo.");
    }
  };

  return (
    <Modal
      title={`Histórico de Planos de Ação`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={1000}
      style={{ top: 20 }}
    >
      <Row style={{ maxHeight: 600, overflowY: "auto" }} gutter={[16, 16]}>
        {(planosAcao?.length === 0 || isError) && (
          <Col
            span={24}
            style={{ textAlign: "center", height: 100, paddingTop: 40 }}
          >
            <Typography.Text type="secondary">
              Nenhum histórico de planos de ação encontrado.
            </Typography.Text>
          </Col>
        )}

        {isLoading && <Skeleton active paragraph={{ rows: 10 }} />}

        {!isLoading && planosAcao && (
          <Col>
            <Typography.Text type="secondary">
              Planos de ação listados do mais recente ao mais antigo.
            </Typography.Text>
          </Col>
        )}

        <Col span={24}>
          {planosAcao?.map((item) => (
            <Col xs={24} key={item.id}>
              <Card
                title={`Plano de Ação`}
                style={{ marginBottom: 16, backgroundColor: "#f6f8fa" }}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <Typography.Text strong>Solução Geral:</Typography.Text>
                    <Typography.Paragraph>
                      {item.solucaoGeral}
                    </Typography.Paragraph>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Typography.Text strong>
                      Prazo Ação Imediata:
                    </Typography.Text>
                    <Typography.Paragraph>
                      {item.prazoAcaoImediata
                        ? dayjs(item.prazoAcaoImediata).format("DD/MM/YYYY")
                        : "-"}
                    </Typography.Paragraph>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Typography.Text strong>Medida Corretiva:</Typography.Text>
                    <Typography.Paragraph>
                      {item.medidaCorretiva}
                    </Typography.Paragraph>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Typography.Text strong>
                      Prazo Ação Corretiva:
                    </Typography.Text>
                    <Typography.Paragraph>
                      {item.prazoAcaoCorretiva
                        ? dayjs(item.prazoAcaoCorretiva).format("DD/MM/YYYY")
                        : "-"}
                    </Typography.Paragraph>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Typography.Text strong>Medida Preventiva:</Typography.Text>
                    <Typography.Paragraph>
                      {item.medidaPreventiva}
                    </Typography.Paragraph>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Typography.Text strong>
                      Prazo Medida Preventiva:
                    </Typography.Text>
                    <Typography.Paragraph>
                      {item.prazoAcaoPreventiva
                        ? dayjs(item.prazoAcaoPreventiva).format("DD/MM/YYYY")
                        : "-"}
                    </Typography.Paragraph>
                  </Col>

                  {item?.anexos?.length > 0 && (
                    <Col xs={24}>
                      <Typography.Text strong>Anexos:</Typography.Text>
                      <List
                        dataSource={item.anexos || []}
                        renderItem={(anexo) => (
                          <List.Item>
                            <Button
                              type="link"
                              onClick={() => handleOpenAnexo(anexo.nomeUpload)}
                            >
                              {anexo.nomeArquivo}
                            </Button>
                          </List.Item>
                        )}
                      />
                    </Col>
                  )}

                  {item.dataParecer && (
                    <>
                      <Divider orientation="left" orientationMargin={0}>
                        <Typography.Title level={5}>
                          Parecer -{" "}
                          {dayjs(item.dataParecer).format("DD/MM/YYYY")}
                        </Typography.Title>
                      </Divider>

                      <Col xs={24} sm={12}>
                        <Typography.Text strong>Parecer:</Typography.Text>
                        <Typography.Paragraph>
                          {SITUACAO_PARECER_MAP[item.situacao]}
                        </Typography.Paragraph>
                      </Col>

                      <Col xs={24} sm={12}>
                        <Typography.Text strong>
                          Registrado por:
                        </Typography.Text>
                        <Typography.Paragraph>
                          {item.monitorInspetor?.nome}
                        </Typography.Paragraph>
                      </Col>

                      <Col xs={24}>
                        <Typography.Text strong>
                          Descrição Parecer:
                        </Typography.Text>
                        <Typography.Paragraph>
                          {item.descricaoParecer}
                        </Typography.Paragraph>
                      </Col>
                    </>
                  )}
                </Row>
              </Card>
            </Col>
          ))}
        </Col>
      </Row>
    </Modal>
  );
}
